import type {
  ModelDuplicateFormDefaultValues,
  ModelDuplicateFormDefaultVariables,
  UseModelDuplicateFormProps,
} from "./model-duplicate-form.type";
import type { ModelItem } from "@/modules/model";

import { Form } from "antd";
import { useTranslation } from "react-i18next";

import { useDuplicate } from "@/hooks/queries";
import { useFormValidation } from "@/modules/form-validation";
import { useModelNavigate } from "@/modules/model-navigate";

export const useModelDuplicateForm = <
  Item extends ModelItem,
  Values extends ModelDuplicateFormDefaultValues,
  Variables extends ModelDuplicateFormDefaultVariables,
  Data extends ModelItem = Item,
>({
  modelKey,
  item,
  initialValues: customInitialValues,
  valuesToVariables,
  validationNames = [],
}: UseModelDuplicateFormProps<Item, Values, Variables>) => {
  const { t } = useTranslation();
  const { navigateToModel } = useModelNavigate();

  const { mutate, isPending, isError, error } = useDuplicate<Data, Variables>({
    modelKey,
    id: item.id,
    onSuccess: ({ id }) => navigateToModel(modelKey, { id }),
  });

  const [form] = Form.useForm<Values>();
  const formId = `${modelKey}-duplicate-form-${item.id}`;

  const defaultInitialValues: ModelDuplicateFormDefaultValues = {
    name: `${item.name} - ${t("global:copy")}`,
  };

  const initialValues = {
    ...defaultInitialValues,
    ...customInitialValues,
  } as Partial<Values>;

  const { validationErrors, validation } = useFormValidation(isError, error, [
    "name",
    ...validationNames,
  ]);

  const onFormSubmit = (values: Values) => {
    const variables = valuesToVariables(values);
    mutate(variables);
  };

  return {
    form,
    formId,
    initialValues,
    onFormSubmit,
    isPending,
    isError,
    validation,
    validationErrors,
  };
};
